import type { NodeId } from "~/models/dto";
import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { Tuple } from "~/tools/tuple";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { getComponentMetricBindingFn, getComponentMetricsWithDataFn } from "../../common/openapi";
import type { UseFullQueryOptions } from "../react-query";

export interface QueryComponentBoundMetricParams {
	componentIdOrIdentifier: string | NodeId;
	metricBindingIdentifier: string;
	timestamp: undefined | Time;
}

export const queryComponentBoundMetric = (({ componentIdOrIdentifier, metricBindingIdentifier, timestamp }) => {
	function queryFn() {
		return getComponentMetricBindingFn({
			componentIdOrIdentifier: componentIdOrIdentifier.toString(),
			metricBindingIdentifier,
			topologyTime: getTime(timestamp),
		});
	}

	return {
		queryKey: Tuple("components", componentIdOrIdentifier, "binding", metricBindingIdentifier),
		queryFn,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		timestamp,
	} as const;
}) satisfies (params: QueryComponentBoundMetricParams) => UseFullQueryOptions;

export interface QueryNonEmptyComponentBoundMetricParams {
	componentIdOrIdentifier: NodeId | string;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const queryNonEmptyComponentBoundMetrics = (({ componentIdOrIdentifier, timestamp, timeRange }) => {
	function queryFn() {
		const interval = timeRange.toInterval();

		return getComponentMetricsWithDataFn({
			componentIdOrIdentifier: componentIdOrIdentifier.toString(),
			topologyTime: getTime(timestamp),
			startTime: getTime(interval.start),
			endTime: getTime(interval.end),
		});
	}

	return {
		queryKey: Tuple("components", componentIdOrIdentifier, "binding"),
		queryFn,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		timestamp,
		timeRange,
	} as const;
}) satisfies (params: QueryNonEmptyComponentBoundMetricParams) => UseFullQueryOptions;
